import { MenuItem } from '@chakra-ui/react';
import { Node } from 'reactflow';
import { gifMenuState } from './GifModal';
import { useAddObjectButton } from './useAddObjectButton';
import { AnchorRect, rectToAnchor } from './AnchoredPanel';
import { Film } from 'lucide-react';
import { useCallback } from 'react';

type Props = {
  targetNode?: Node;
  sourceNode?: Node;
  getTargetNodeId?: () => void;
  // Returns the "+" button rect the picker anchors to. The caller owns the
  // button ref; this falls back to the clicked menu item if not provided.
  getAnchor?: () => AnchorRect | null;
};

function AddGifButton({ targetNode, getTargetNodeId, sourceNode, getAnchor }: Props) {
  const emptyBlock = useAddObjectButton({
    id: sourceNode?.id,
    sourceHandle: sourceNode?.id,
  });

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const targetNodeId = typeof targetNode?.id === 'string' ? targetNode.id : getTargetNodeId?.();
      let resolvedTargetId: string | undefined;
      if (typeof targetNodeId === 'string') {
        resolvedTargetId = targetNodeId;
      } else if (sourceNode) {
        const newBlockId = emptyBlock();
        if (typeof newBlockId === 'string') {
          resolvedTargetId = newBlockId;
        }
      } else {
        throw new Error('No targetNode or sourceNode');
      }

      if (!resolvedTargetId) {
        return;
      }

      const anchorRect = getAnchor?.() ?? rectToAnchor(event.currentTarget);

      const targetId = resolvedTargetId;
      // Open on the next frame: clicking this item also closes the add-block
      // menu, and opening synchronously lets the menu's focus-return register as
      // an outside interaction that immediately dismisses the picker.
      requestAnimationFrame(() => {
        gifMenuState.targetNodeId = targetId;
        gifMenuState.anchorRect = anchorRect;
        gifMenuState.open();
      });
    },
    [targetNode, getTargetNodeId, sourceNode, emptyBlock, getAnchor],
  );

  return (
    <MenuItem onClick={onClick} icon={<Film size="16px" />}>
      GIF
    </MenuItem>
  );
}

export default AddGifButton;
